import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Settings, Flag, Activity,
  Megaphone, Power, LogOut, Menu, X, Shield, KeyRound,
  Eye, EyeOff, CheckCircle, Search, RefreshCw, Trash2,
  UserCheck, UserX, ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '../api';
import FeatureFlagsComponent from '../components/admin/FeatureFlags';

/**
 * Admin Dashboard - Main Page
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin/login');
  };

  const menuItems = [
    { id: 'dashboard',       label: 'Dashboard',       icon: LayoutDashboard },
    { id: 'users',           label: 'User Management', icon: Users },
    { id: 'campaigns',       label: 'Campaigns',       icon: Megaphone },
    { id: 'features',        label: 'Feature Flags',   icon: Flag },
    { id: 'config',          label: 'System Config',   icon: Settings },
    { id: 'activity',        label: 'Activity Logs',   icon: Activity },
    { id: 'changePassword',  label: 'Change Password', icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`bg-gray-900 text-white transition-all duration-300 ${
        sidebarOpen ? 'w-64' : 'w-20'
      }`}>
        <div className="p-4 flex items-center justify-between border-b border-gray-700">
          {sidebarOpen && <h1 className="text-xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Admin Portal
          </h1>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <nav className="mt-6">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800 transition ${
                  activeTab === item.id ? 'bg-gray-800 border-l-4 border-blue-500' : ''
                }`}
              >
                <Icon className="w-5 h-5" />
                {sidebarOpen && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-full border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800 text-red-400"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Dashboard Stats */}
          {activeTab === 'dashboard' && (
            <div>
              <div className="app-card rounded-[2rem] p-6 sm:p-8 mb-8">
                <div className="section-kicker mb-4">Admin overview</div>
                <h2 className="text-3xl font-bold text-gray-800">Dashboard</h2>
                <p className="mt-3 text-slate-600">Track platform growth, campaign activity, and system health from one clearer internal workspace.</p>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Total Users */}
                  <div className="metric-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Total Users</p>
                        <p className="text-3xl font-bold text-gray-800">{stats?.totalUsers || 0}</p>
                      </div>
                      <Users className="w-12 h-12 text-blue-500" />
                    </div>
                  </div>

                  {/* Total Brands */}
                  <div className="metric-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Total Brands</p>
                        <p className="text-3xl font-bold text-gray-800">{stats?.totalBrands || 0}</p>
                      </div>
                      <Users className="w-12 h-12 text-green-500" />
                    </div>
                  </div>

                  {/* Total creators */}
                  <div className="metric-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Total creators</p>
                        <p className="text-3xl font-bold text-gray-800">{stats?.totalcreators || 0}</p>
                      </div>
                      <Users className="w-12 h-12 text-purple-500" />
                    </div>
                  </div>

                  {/* Total Campaigns */}
                  <div className="metric-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Total Campaigns</p>
                        <p className="text-3xl font-bold text-gray-800">{stats?.totalCampaigns || 0}</p>
                      </div>
                      <Megaphone className="w-12 h-12 text-orange-500" />
                    </div>
                  </div>

                  {/* Active Campaigns */}
                  <div className="metric-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Active Campaigns</p>
                        <p className="text-3xl font-bold text-gray-800">{stats?.activeCampaigns || 0}</p>
                      </div>
                      <Power className="w-12 h-12 text-green-600" />
                    </div>
                  </div>

                  {/* Total Bids */}
                  <div className="metric-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Total Bids</p>
                        <p className="text-3xl font-bold text-gray-800">{stats?.totalBids || 0}</p>
                      </div>
                      <Activity className="w-12 h-12 text-blue-600" />
                    </div>
                  </div>

                  {/* Pending Bids */}
                  <div className="metric-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Pending Bids</p>
                        <p className="text-3xl font-bold text-gray-800">{stats?.pendingBids || 0}</p>
                      </div>
                      <Activity className="w-12 h-12 text-yellow-600" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Render other tabs */}
          {activeTab === 'users'          && <UserManagement />}
          {activeTab === 'campaigns'      && <CampaignManagement />}
          {activeTab === 'features'       && <FeatureFlagsComponent />}
          {activeTab === 'config'         && <SystemConfig />}
          {activeTab === 'activity'       && <ActivityLogs />}
          {activeTab === 'changePassword' && <ChangePassword />}
        </div>
      </main>
    </div>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [twoFactorFilter, setTwoFactorFilter] = useState('ALL');
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionUserId, setActionUserId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const adminUser = useMemo(() => {
    const raw = localStorage.getItem('adminUser');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (parseError) {
      return null;
    }
  }, []);

  const adminUsername = adminUser?.username || 'admin';
  const adminId = adminUser?.id ?? null;

  const filteredUsers = useMemo(() => users.filter((user) => {
    if (roleFilter !== 'ALL' && user.role !== roleFilter) {
      return false;
    }
    if (statusFilter !== 'ALL') {
      const isActive = Boolean(user.isActive);
      if (statusFilter === 'ACTIVE' && !isActive) {
        return false;
      }
      if (statusFilter === 'SUSPENDED' && isActive) {
        return false;
      }
    }
    if (twoFactorFilter !== 'ALL') {
      const has2FA = Boolean(user.twoFactorEnabled);
      if (twoFactorFilter === 'ENABLED' && !has2FA) {
        return false;
      }
      if (twoFactorFilter === 'DISABLED' && has2FA) {
        return false;
      }
    }
    return true;
  }), [users, roleFilter, statusFilter, twoFactorFilter]);

  const totalUsers = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pageStart = currentPage * pageSize;
  const pagedUsers = filteredUsers.slice(pageStart, pageStart + pageSize);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (searchQuery.trim()) {
        const response = await api.get('/admin/users/search', {
          params: { query: searchQuery.trim() }
        });
        setUsers(Array.isArray(response.data) ? response.data : []);
      } else {
        const response = await api.get('/admin/users', {
          params: { page: 0, size: 1000 }
        });
        setUsers(Array.isArray(response.data?.content) ? response.data.content : []);
      }
    } catch (fetchError) {
      setError(fetchError.response?.data?.error || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(0);
    setSearchQuery(searchInput);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchQuery('');
    setRoleFilter('ALL');
    setStatusFilter('ALL');
    setTwoFactorFilter('ALL');
    setSelectedUser(null);
    setPage(0);
  };

  const handleToggleStatus = async (user) => {
    if (adminId && user.id === adminId) {
      setError('You cannot change your own admin account status.');
      return;
    }
    setActionUserId(user.id);
    setError('');
    setSuccess('');
    try {
      await api.put(`/admin/users/${user.id}/toggle-status`, null, {
        params: { adminUsername }
      });
      setSuccess(`User ${user.isActive ? 'suspended' : 'activated'} successfully.`);
      await loadUsers();
      if (selectedUser?.id === user.id) {
        setSelectedUser((prev) => (prev ? { ...prev, isActive: !prev.isActive } : prev));
      }
    } catch (toggleError) {
      setError(toggleError.response?.data?.error || 'Failed to update user status');
    } finally {
      setActionUserId(null);
    }
  };

  const handleDeleteUser = async (user) => {
    if (adminId && user.id === adminId) {
      setError('You cannot delete your own admin account.');
      return;
    }
    const confirmation = window.prompt(`Type DELETE to permanently remove ${user.email}`);
    if (confirmation !== 'DELETE') {
      return;
    }
    setActionUserId(user.id);
    setError('');
    setSuccess('');
    try {
      await api.delete(`/admin/users/${user.id}`, {
        params: { adminUsername }
      });
      setSuccess('User deleted successfully.');
      if (selectedUser?.id === user.id) {
        setSelectedUser(null);
      }
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionUserId(null);
    }
  };

  return (
    <div>
      <div className="app-card rounded-[2rem] p-6 sm:p-8 mb-6">
        <div className="section-kicker mb-4">Admin controls</div>
        <h2 className="text-3xl font-bold text-gray-800">User Management</h2>
        <p className="mt-3 text-slate-600">Search users, filter by role/status, and perform moderation actions safely.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-4">
            <label htmlFor="admin-user-search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                id="admin-user-search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by name or email"
                className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={roleFilter}
              onChange={(event) => {
                setRoleFilter(event.target.value);
                setPage(0);
              }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ALL">All roles</option>
              <option value="CREATOR">Creator</option>
              <option value="BRAND">Brand</option>
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ALL">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">2FA</label>
            <select
              value={twoFactorFilter}
              onChange={(event) => {
                setTwoFactorFilter(event.target.value);
                setPage(0);
              }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ALL">All</option>
              <option value="ENABLED">Enabled</option>
              <option value="DISABLED">Disabled</option>
            </select>
          </div>

          <div className="lg:col-span-2 flex gap-2">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 text-sm font-medium"
            >
              Search
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center justify-center gap-1 border border-slate-300 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg border border-green-200 bg-green-50 text-green-700 text-sm">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <p className="text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{pagedUsers.length}</span> of <span className="font-semibold text-slate-900">{totalUsers}</span> users
            </p>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">Rows</span>
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(0);
                }}
                className="border border-slate-300 rounded-md px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-500">Loading users...</div>
          ) : pagedUsers.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No users found for the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-slate-600">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Credits</th>
                    <th className="px-4 py-3 font-medium">2FA</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.map((user) => {
                    const isActionLoading = actionUserId === user.id;
                    const isSelf = adminId && user.id === adminId;
                    return (
                      <tr key={user.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="text-blue-700 hover:text-blue-900 font-medium text-left"
                          >
                            {user.name || 'Unnamed'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{user.email}</td>
                        <td className="px-4 py-3">{user.role}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            user.isActive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {user.isActive ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="px-4 py-3">{user.credits ?? 0}</td>
                        <td className="px-4 py-3">
                          {user.twoFactorEnabled ? (
                            <span className="text-green-700 font-medium">Enabled</span>
                          ) : (
                            <span className="text-slate-500">Disabled</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggleStatus(user)}
                              disabled={isActionLoading || isSelf}
                              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${
                                user.isActive
                                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {user.isActive ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                              {user.isActive ? 'Suspend' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              disabled={isActionLoading || isSelf}
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-sm">
            <p className="text-slate-600">
              Page <span className="font-semibold text-slate-900">{currentPage + 1}</span> of <span className="font-semibold text-slate-900">{totalPages}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                className="inline-flex items-center gap-1 border border-slate-300 rounded-md px-2.5 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </button>
              <button
                onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage >= totalPages - 1}
                className="inline-flex items-center gap-1 border border-slate-300 rounded-md px-2.5 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="xl:col-span-4 bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-3">User Details</h3>
          {!selectedUser ? (
            <p className="text-sm text-slate-500">Select a user name from the table to view details.</p>
          ) : (
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-slate-500">User ID</dt>
                <dd className="font-medium text-slate-900">{selectedUser.id}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Name</dt>
                <dd className="font-medium text-slate-900">{selectedUser.name || 'Unnamed'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Email</dt>
                <dd className="font-medium text-slate-900">{selectedUser.email}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Role</dt>
                <dd className="font-medium text-slate-900">{selectedUser.role}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Status</dt>
                <dd className="font-medium text-slate-900">{selectedUser.isActive ? 'Active' : 'Suspended'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Credits</dt>
                <dd className="font-medium text-slate-900">{selectedUser.credits ?? 0}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Two-Factor Authentication</dt>
                <dd className="font-medium text-slate-900">{selectedUser.twoFactorEnabled ? 'Enabled' : 'Disabled'}</dd>
              </div>
            </dl>
          )}
        </div>
      </div>
    </div>
  );
};

const CampaignManagement = () => <div className="text-gray-800">Campaign Management (Coming next)</div>;
const SystemConfig     = () => <div className="text-gray-800">System Config (Coming next)</div>;
const ActivityLogs     = () => <div className="text-gray-800">Activity Logs (Coming next)</div>;

// Defined at module level so React never treats it as a new component type on re-render.
// Defining it inside ChangePassword would recreate the type every keystroke → input loses focus.
const PasswordField = ({ id, label, value, showKey, placeholder, show, setShow, setForm, setErrors, errors }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <KeyRound className="h-4 w-4 text-gray-400" />
      </div>
      <input
        id={id}
        type={show[showKey] ? 'text' : 'password'}
        value={value}
        onChange={(e) => {
          setForm(f => ({ ...f, [id]: e.target.value }));
          setErrors(er => ({ ...er, [id]: '' }));
        }}
        placeholder={placeholder}
        className={`block w-full pl-9 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 ${
          errors[id] ? 'border-red-400 bg-red-50' : 'border-gray-300'
        }`}
      />
      <button
        type="button"
        onClick={() => setShow(s => ({ ...s, [showKey]: !s[showKey] }))}
        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
      >
        {show[showKey] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
    {errors[id] && <p className="mt-1 text-xs text-red-600">{errors[id]}</p>}
  </div>
);

// ─── Change Password ──────────────────────────────────────────────────────────
const ChangePassword = () => {
  const [form, setForm]       = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [show, setShow]       = useState({ current: false, newPw: false, confirm: false });

  const validate = () => {
    const e = {};
    if (!form.currentPassword)              e.currentPassword = 'Current password is required';
    if (!form.newPassword)                  e.newPassword = 'New password is required';
    else if (form.newPassword.length < 8)   e.newPassword = 'Must be at least 8 characters';
    if (form.newPassword === form.currentPassword && form.currentPassword)
                                            e.newPassword = 'New password must differ from current';
    if (!form.confirmPassword)              e.confirmPassword = 'Please confirm your new password';
    else if (form.newPassword !== form.confirmPassword)
                                            e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      setSuccess(false);
      await api.post('/admin/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword:     form.newPassword,
      });
      setSuccess(true);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setErrors({});
    } catch (err) {
      setErrors({ api: err.response?.data?.message || 'Failed to change password. Try again.' });
    } finally {
      setLoading(false);
    }
  };

  const fieldProps = { show, setShow, setForm, setErrors, errors };

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-8">Change Password</h2>

      <div className="max-w-md bg-white rounded-lg shadow p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-800">Update Admin Password</p>
            <p className="text-sm text-gray-500">Choose a strong password (min. 8 characters)</p>
          </div>
        </div>

        {errors.api && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {errors.api}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-700">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            Password changed successfully!
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <PasswordField id="currentPassword" label="Current Password"     value={form.currentPassword} showKey="current" placeholder="Enter current password" {...fieldProps} />
          <PasswordField id="newPassword"     label="New Password"         value={form.newPassword}     showKey="newPw"   placeholder="Enter new password"     {...fieldProps} />
          <PasswordField id="confirmPassword" label="Confirm New Password" value={form.confirmPassword} showKey="confirm" placeholder="Re-enter new password"   {...fieldProps} />

          {form.newPassword && (
            <ul className="text-xs text-gray-500 space-y-1 pl-1">
              <li className={form.newPassword.length >= 8          ? 'text-green-600' : 'text-red-500'}>
                {form.newPassword.length >= 8 ? '✓' : '✗'} At least 8 characters
              </li>
              <li className={/[A-Z]/.test(form.newPassword)        ? 'text-green-600' : 'text-gray-400'}>
                {/[A-Z]/.test(form.newPassword) ? '✓' : '○'} Uppercase letter
              </li>
              <li className={/[0-9]/.test(form.newPassword)        ? 'text-green-600' : 'text-gray-400'}>
                {/[0-9]/.test(form.newPassword) ? '✓' : '○'} Number
              </li>
              <li className={/[^A-Za-z0-9]/.test(form.newPassword) ? 'text-green-600' : 'text-gray-400'}>
                {/[^A-Za-z0-9]/.test(form.newPassword) ? '✓' : '○'} Special character
              </li>
            </ul>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 rounded-lg text-white font-medium transition ${
              loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Changing…</span></>
              : <><KeyRound className="w-4 h-4" /><span>Change Password</span></>
            }
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;

